import { Chrome, Clock, Search, Shield, Calendar, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CHROME_WEB_STORE_URL } from "@/config/extension";

const features = [
  {
    icon: <Chrome className="w-8 h-8 text-primary" />,
    title: "Instant Setup",
    description: "No downloads, no installations, no waiting. Just add the extension to Chrome and start blocking immediately.",
    benefits: [
      "One-click installation from Chrome Web Store",
      "No system restarts required",
      "Works instantly across all Chrome tabs"
    ]
  },
  {
    icon: <Search className="w-8 h-8 text-primary" />,
    title: "Smart Keyword Blocking",
    description: "Block unwanted search results before they even appear. Your policies automatically filter search queries.",
    benefits: [
      "Real-time search filtering",
      "Custom keyword policies",
      "Blocks inappropriate search suggestions"
    ]
  },
  {
    icon: <Clock className="w-8 h-8 text-primary" />,
    title: "Scheduled Productivity",
    description: "Set your focus hours in the calendar and let Fendly automatically enforce your blocking policies.",
    benefits: [
      "Calendar-based scheduling",
      "Time-zone aware blocking",
      "Flexible daily/weekly patterns"
    ]
  },
  {
    icon: <Shield className="w-8 h-8 text-primary" />,
    title: "Real-time Protection",
    description: "Blocks threats and unwanted content as you browse, with zero delay or performance impact.",
    benefits: [
      "Instant content blocking",
      "No performance slowdown",
      "Always-on protection"
    ]
  }
];

export default function ChromeExtensionSection() {
  return (
    <section className="py-16 md:py-32 bg-gradient-to-br from-background to-muted/30">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Chrome className="w-5 h-5" />
            <span className="text-sm font-medium">Chrome Extension</span>
          </div>
          <h2 className="text-3xl font-semibold mb-4">
            The Only Content Filtering Extension You'll Ever Need
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            Unlike other solutions that require complex installations or system changes, 
            Fendly works instantly through a simple Chrome Extension. Set your rules once, 
            and get comprehensive protection across all your browsing.
          </p>
          
          {/* Install Extension CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-gradient-to-r from-[#00F5A0] to-[#0AEFFF] text-[#0A0F0D] font-semibold hover:opacity-90"
            >
              <a 
                href={CHROME_WEB_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Chrome className="w-5 h-5" />
                Install Chrome Extension
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="/auth">Start Free Trial</a>
            </Button>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid gap-8 md:grid-cols-2">
          {features.map((feature, index) => (
            <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
                <p className="text-muted-foreground">{feature.description}</p>
              </CardHeader>
              
              <CardContent>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start gap-2 text-sm">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* How It Works */}
        <div className="mt-20">
          <h3 className="text-2xl font-semibold text-center mb-12">How the Extension Works</h3>
          <div className="grid gap-8 md:grid-cols-3">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary">1</span>
              </div>
              <h4 className="font-semibold mb-2">Add Extension</h4>
              <p className="text-sm text-muted-foreground">
                Install from Chrome Web Store in one click
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary">2</span>
              </div>
              <h4 className="font-semibold mb-2">Set Your Rules</h4>
              <p className="text-sm text-muted-foreground">
                Configure policies in your Fendly dashboard
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary">3</span>
              </div>
              <h4 className="font-semibold mb-2">Start Blocking</h4>
              <p className="text-sm text-muted-foreground">
                Extension automatically enforces your policies
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-muted/50 rounded-2xl p-8 max-w-2xl mx-auto">
            <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Ready to Experience Instant Protection?</h3>
            <p className="text-muted-foreground mb-6">
              Get started in under 2 minutes with our Chrome Extension
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary">
                <a href="/auth">Start Free Trial</a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="/dashboard">View Demo</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
